import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useState } from "react";
import "./BulkActions.css";

const BULK_AUTO_FIX_MUTATION = gql`
  mutation BulkAutoFix($orderIds: [String!]!) {
    bulkAutoFix(orderIds: $orderIds) {
      totalSelected
      successCount
      failedCount
      manualReviewRequired
    }
  }
`;

type BulkFixResult = {
  totalSelected: number;
  successCount: number;
  failedCount: number;
  manualReviewRequired: string[];
};

type BulkActionsProps = {
  selectedIds: string[];
  onBulkAutoFix: () => void;
};

export const BulkActions = ({ selectedIds, onBulkAutoFix }: BulkActionsProps) => {
  const [result, setResult] = useState<BulkFixResult | null>(null);

  const [bulkAutoFix, { loading }] = useMutation(BULK_AUTO_FIX_MUTATION, {
    variables: { orderIds: selectedIds },
    onCompleted: (data) => {
      setResult(data.bulkAutoFix);
      onBulkAutoFix();
    },
  });

  const handleBulkAutoFix = () => {
    setResult(null);
    bulkAutoFix();
  };

  const hasSelection = selectedIds.length > 0;

  return (
    <div className="BulkActions">
      <div className="BulkActions__toolbar">
        <span className="BulkActions__count">
          {selectedIds.length} ordre{selectedIds.length !== 1 ? "r" : ""} valgt
        </span>

        <button
          className="BulkActions__btn"
          onClick={handleBulkAutoFix}
          disabled={!hasSelection || loading}
          type="button"
        >
          {loading ? (
            <>
              <span className="BulkActions__spinner" />
              Retter...
            </>
          ) : (
            <>🔧 Auto-ret valgte</>
          )}
        </button>
      </div>

      {result && (
        <div className="BulkActions__result">
          <div className="BulkActions__result-header">Resultat</div>
          <div className="BulkActions__result-stats">
            <div className="BulkActions__stat">
              <span className="BulkActions__stat-value BulkActions__stat-value--success">
                {result.successCount}
              </span>
              <span className="BulkActions__stat-label">rettet</span>
            </div>
            <div className="BulkActions__stat">
              <span className="BulkActions__stat-value BulkActions__stat-value--failed">
                {result.failedCount}
              </span>
              <span className="BulkActions__stat-label">fejlet</span>
            </div>
            {result.manualReviewRequired.length > 0 && (
              <div className="BulkActions__stat">
                <span className="BulkActions__stat-value BulkActions__stat-value--review">
                  {result.manualReviewRequired.length}
                </span>
                <span className="BulkActions__stat-label">
                  kræver manuel gennemgang
                </span>
              </div>
            )}
          </div>
          {result.manualReviewRequired.length > 0 && (
            <div className="BulkActions__review-list">
              <span className="BulkActions__review-label">
                Ordrer der kræver manuel rettelse:
              </span>
              <ul className="BulkActions__review-ids">
                {result.manualReviewRequired.map((id) => (
                  <li key={id} className="BulkActions__review-id">
                    {id}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
